import liftService from '../../services/liftService'
import { Dispatch } from 'redux'
import { createSlice } from '@reduxjs/toolkit'
import { createNotification } from './notifications'
import Lift from '../../types/Lift'
import i18next from 'i18next'
import { AxiosRequestConfig } from 'axios'

interface LiftsState {
  lifts: Lift[]
  lift: Lift | undefined
  show: boolean
  loading: boolean
}

const initialState: LiftsState = {
  lifts: [],
  lift: undefined,
  show: false,
  loading: false
}

const liftSlice = createSlice({
  name: 'lifts',
  initialState,
  reducers: {
    setLifts(state, action) {
      state.lifts = action.payload
    },
    setLift(state, action) {
      state.lift = action.payload
    },
    createLift(state, action) {
      state.lifts = [...state.lifts, action.payload].sort((a, b) => a.name.localeCompare(b.name))
    },
    updateLift(state, action) {
      state.lifts = state.lifts
        .map((lift) => (lift.id === action.payload.id ? action.payload : lift))
        .toSorted((a, b) => a.name.localeCompare(b.name))
    },
    deleteLift(state, action) {
      state.lifts = state.lifts.filter((l) => l.id !== action.payload)
    },
    showLift(state, action) {
      state.lift = action.payload
      state.show = true
    },
    hideLift(state) {
      state.show = false
      state.lift = undefined
    },
    setLoading(state, action) {
      state.loading = action.payload
    }
  }
})

export const { createLift, updateLift, setLifts } = liftSlice.actions

export function getLifts(config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(liftSlice.actions.setLoading(true))
      const lifts = await liftService.getLifts(config)
      dispatch(liftSlice.actions.setLifts(lifts))
    } finally {
      dispatch(liftSlice.actions.setLoading(false))
    }
  }
}

export function getLiftsByProjectId(id: number, config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(liftSlice.actions.setLoading(true))
      const lifts = await liftService.getLiftsByProjectId(id, config)
      dispatch(liftSlice.actions.setLifts(lifts))
    } finally {
      dispatch(liftSlice.actions.setLoading(false))
    }
  }
}

export function getLift(id: number, config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(liftSlice.actions.setLoading(true))
      const lift = await liftService.getLift(id, config)
      dispatch(liftSlice.actions.setLift(lift))
    } finally {
      dispatch(liftSlice.actions.setLoading(false))
    }
  }
}

export function deleteLift(id: number, config?: AxiosRequestConfig) {
  return async (dispatch: Dispatch) => {
    try {
      await liftService.deleteLift(id, config)
      dispatch(liftSlice.actions.deleteLift(id))

      // FIXME: Translate notification message.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'success',
          message: i18next.t('liftDeleteSuccess', { ns: 'common' }),
          timeout: 5000
        })
      )
    } catch (_error) {
      // FIXME: Translate notification message.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        createNotification({
          show: true,
          type: 'warning',
          message: 'Something went wrong',
          timeout: 0
        })
      )
    }
  }
}

export function showLift(lift?: Lift) {
  return async (dispatch: Dispatch) => {
    dispatch(liftSlice.actions.showLift(lift))
  }
}

export function hideLift() {
  return async (dispatch: Dispatch) => {
    dispatch(liftSlice.actions.hideLift())
  }
}

export default liftSlice.reducer
